import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";
import type { Subscriber as ApiSubscriber } from "../ApiTypes";
import { Avatar } from "../Avatar";
import { RadioGroup, Radio } from "./components/RadioGroup";
import { SubscribersSelectorModal } from "./components/SubscribersSelectorModal";
import { sortSubscribersByName } from "./utils";

export function SubscribersSelector({
  subscribers,
  selectedSubscribers,
  onSelectedSubscribersChange,
  subscriptionType,
  onSubscriptionTypeChange,
  alwaysNotify,
  allSubscribersLabel,
  testIdPrefix = "subscribe",
}: SubscribersSelector.Props) {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);

  // Automatically open modal when switching to SELECTED option
  useEffect(() => {
    if (subscriptionType === SubscribersSelector.SubscriptionOption.SELECTED) {
      setShowModal(true);
    }
  }, [subscriptionType]);

  if (subscribers.length === 0) return null;

  if (alwaysNotify.length >= subscribers.length) {
    return <AlwaysNotifyOnly subscribers={subscribers} allSubscribersLabel={allSubscribersLabel} />;
  }

  const handleSaveSelection = (selected: SubscribersSelector.Subscriber[]) => {
    onSelectedSubscribersChange(selected);
  };

  return (
    <div>
      <p className="font-bold mb-1.5">{t("When I post this, notify:")}</p>

      <RadioGroup
        name="subscriptions-options"
        onChange={(value) => onSubscriptionTypeChange(value as SubscribersSelector.SubscriptionOption)}
        defaultValue={subscriptionType}
      >
        <SubscriptionOptionItem
          label={allSubscribersLabel}
          value={SubscribersSelector.SubscriptionOption.ALL}
          subscribers={subscribers}
          isSelected={subscriptionType === SubscribersSelector.SubscriptionOption.ALL}
          testId={`${testIdPrefix}-all`}
        />

        <div onClick={() => setShowModal(true)}>
          <SubscriptionOptionItem
            label={t("Only the people I select")}
            value={SubscribersSelector.SubscriptionOption.SELECTED}
            subscribers={selectedSubscribers}
            isSelected={subscriptionType === SubscribersSelector.SubscriptionOption.SELECTED}
            testId={`${testIdPrefix}-specific-people`}
          />
        </div>

        <SubscriptionOptionItem
          label={t("No one")}
          value={SubscribersSelector.SubscriptionOption.NONE}
          subscribers={[]}
          isSelected={subscriptionType === SubscribersSelector.SubscriptionOption.NONE}
          testId={`${testIdPrefix}-no-one`}
        />
      </RadioGroup>

      <SubscribersSelectorModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        subscribers={subscribers}
        selectedSubscribers={selectedSubscribers}
        alwaysNotify={alwaysNotify}
        onSave={handleSaveSelection}
      />
    </div>
  );
}

function AlwaysNotifyOnly({
  subscribers,
  allSubscribersLabel,
}: {
  subscribers: SubscribersSelector.Subscriber[];
  allSubscribersLabel: string;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <p className="font-bold mb-1.5">{t("When I post this, notify:")}</p>
      <div className="my-1">
        <p className="text-content-accent">{allSubscribersLabel}</p>
        <SelectedPeople subscribers={subscribers} hide={false} indent={false} />
      </div>
    </div>
  );
}

interface SubscriptionOptionItemProps {
  label: string;
  value: SubscribersSelector.SubscriptionOption;
  subscribers: SubscribersSelector.Subscriber[];
  isSelected: boolean;
  testId: string;
}

function SubscriptionOptionItem({ label, value, subscribers, isSelected, testId }: SubscriptionOptionItemProps) {
  return (
    <div className="my-1">
      <Radio label={label} value={value} testId={testId} />
      <SelectedPeople subscribers={subscribers} hide={!isSelected} />
    </div>
  );
}

function SelectedPeople({
  subscribers,
  hide,
  indent = true,
}: {
  subscribers: SubscribersSelector.Subscriber[];
  hide: boolean;
  indent?: boolean;
}) {
  if (subscribers.length === 0 || hide) return null;

  const sortedSubscribers = sortSubscribersByName(subscribers);

  return (
    <div className={["flex flex-wrap gap-2", indent ? "ml-6" : ""].join(" ")}>
      {sortedSubscribers.map((subscriber) => {
        if (!subscriber.person) return null;
        return <Avatar person={subscriber.person} size="tiny" key={subscriber.person.id} />;
      })}
    </div>
  );
}

export namespace SubscribersSelector {
  export type Subscriber = ApiSubscriber;

  export enum SubscriptionOption {
    ALL = "all",
    SELECTED = "selected",
    NONE = "none",
  }

  export interface Props {
    subscribers: Subscriber[];
    selectedSubscribers: Subscriber[];
    onSelectedSubscribersChange: (subscribers: Subscriber[]) => void;
    subscriptionType: SubscriptionOption;
    onSubscriptionTypeChange: (type: SubscriptionOption) => void;
    alwaysNotify: Subscriber[];
    allSubscribersLabel: string;
    testIdPrefix?: string;
  }
}
